import { z } from 'zod';

/** Money arrives as a decimal string so no precision is lost. null = no data. */
export const decimalSchema = z.string().nullable();

export const roleSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
});

export const currentUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  full_name: z.string(),
  status: z.string(),
  is_protected: z.boolean(),
  last_login_at: z.string().nullable().optional(),
  role: roleSchema,
  permissions: z.array(z.string()),
  company_id: z.string(),
});

export const businessUnitSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.enum([
    'RESTAURANT',
    'PRODUCTION_KITCHEN',
    'CAFE',
    'BAKERY',
    'WAREHOUSE',
    'OTHER',
  ]),
  status: z.enum(['ACTIVE', 'OPENING', 'ARCHIVED']),
  sort_order: z.number().optional().default(0),
});

export const periodSchema = z.object({
  id: z.string(),
  year: z.number(),
  month: z.number(),
  status: z.enum(['DRAFT', 'UNDER_REVIEW', 'CLOSED']),
  closed_at: z.string().nullable().optional(),
  closed_by_id: z.string().nullable().optional(),
  reopened_at: z.string().nullable().optional(),
  reopened_by_id: z.string().nullable().optional(),
  reopen_reason: z.string().nullable().optional(),
});

export const periodRefSchema = z.object({
  id: z.string(),
  year: z.number(),
  month: z.number(),
  status: z.string(),
});

export const businessUnitRefSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.string(),
});

export const moneySchema = z.object({
  value: decimalSchema,
  mom: decimalSchema,
});

/** One confirmed month's figures. A key absent from `values` means "no data"
 * for that metric — it is never filled in as 0. */
export const monthlySeriesPointSchema = z.object({
  period_id: z.string(),
  year: z.number(),
  month: z.number(),
  label: z.string(),
  values: z.record(decimalSchema),
});

/** `current − previous` for one leaf expense category — parent aggregates are
 * never included, they would double count what their children already report. */
export const expenseDriverRowSchema = z.object({
  category_id: z.string(),
  code: z.string(),
  name: z.string(),
  current: decimalSchema,
  previous: decimalSchema,
  delta: decimalSchema,
});

// Duplicated from collab.ts's alertSchema rather than imported, to avoid a
// circular module dependency (collab.ts imports `decimalSchema` from here).
export const dashboardAlertSchema = z.object({
  rule: z.string(),
  severity: z.enum(['INFO', 'WARNING', 'CRITICAL']),
  title: z.string(),
  detail: z.string(),
  business_unit_id: z.string().nullable(),
  metric: z.string().nullable().optional(),
  value: decimalSchema.optional(),
  threshold: decimalSchema.optional(),
});

/** One top-level spend group of a cost center, for the selected month. */
export const costCenterGroupSchema = z.object({
  code: z.string(),
  name: z.string(),
  amount: decimalSchema,
  mom_abs: decimalSchema,
  mom_pct: decimalSchema,
});

/** A node of the real category hierarchy under a spend group. Recursive —
 * the depth is the workbook's, not a fixed two or three levels. */
export interface CostCenterNode {
  category_id: string;
  code: string;
  name: string;
  level: number;
  /** The selected month. */
  amount: string | null;
  /** Every confirmed month, keyed "2026-07". */
  monthly: Record<string, string | null>;
  children: CostCenterNode[];
}

// Input is `unknown`, not CostCenterNode: `.default()` makes those keys
// optional on the way in, which a same-type-in-and-out annotation rejects.
export const costCenterNodeSchema: z.ZodType<CostCenterNode, z.ZodTypeDef, unknown> = z.lazy(() =>
  z.object({
    category_id: z.string(),
    code: z.string(),
    name: z.string(),
    level: z.number(),
    amount: decimalSchema,
    monthly: z.record(decimalSchema).default({}),
    children: z.array(costCenterNodeSchema).default([]),
  }),
);

export const costCenterMonthlyPointSchema = z.object({
  period_id: z.string(),
  year: z.number(),
  month: z.number(),
  label: z.string(),
  all_expenses: decimalSchema,
  groups: z.record(decimalSchema),
});

export const costCenterYtdSchema = z.object({
  all_expenses: decimalSchema,
  groups: z.record(decimalSchema),
  months: z.number(),
});

/** Spend-only analytics for a production kitchen. Present only when the unit
 * is a PRODUCTION_KITCHEN — a cost center has no revenue, profit or margin. */
export const costCenterAnalyticsSchema = z.object({
  all_expenses: costCenterGroupSchema,
  groups: z.array(costCenterGroupSchema).default([]),
  monthly_series: z.array(costCenterMonthlyPointSchema).default([]),
  ytd: costCenterYtdSchema,
  leaf_breakdown: z.array(costCenterNodeSchema).default([]),
  top_drivers_up: z.array(expenseDriverRowSchema).default([]),
  top_drivers_down: z.array(expenseDriverRowSchema).default([]),
});

export const healthSummarySchema = z.object({
  status: z.enum(['ok', 'warning', 'critical']),
  critical_count: z.number().default(0),
  warning_count: z.number().default(0),
  info_count: z.number().default(0),
});

export const kpiResponseSchema = z.object({
  business_unit: businessUnitRefSchema,
  // Null until the first workbook is imported — not an error, an empty state.
  period: periodRefSchema.nullable().optional().default(null),
  has_data: z.boolean(),
  kpis: z.record(moneySchema),

  // --- dashboard extensions ---
  selected_period: periodRefSchema.nullable().optional().default(null),
  available_periods: z.array(periodRefSchema).default([]),
  current: z.record(decimalSchema).default({}),
  previous_period_deltas: z.record(decimalSchema).default({}),
  monthly_series: z.array(monthlySeriesPointSchema).default([]),
  ytd: z.record(decimalSchema).default({}),
  expense_drivers: z.array(expenseDriverRowSchema).default([]),
  alerts: z.array(dashboardAlertSchema).default([]),
  health: healthSummarySchema.default({ status: 'ok', critical_count: 0, warning_count: 0, info_count: 0 }),

  // Production kitchens only; null for every restaurant.
  cost_center: costCenterAnalyticsSchema.nullable().optional().default(null),
});

/** SOURCE — a stored value; SUBTOTAL — the sum of its children; CALCULATED —
 * a formula; RATIO — a percentage of revenue. Only SOURCE rows exist as
 * FinancialValue: the rest are rebuilt for display so nothing is counted
 * twice in the database. */
export const ROW_KINDS = ['SOURCE', 'SUBTOTAL', 'CALCULATED', 'RATIO'] as const;

export const pnlRowSchema = z.object({
  // Null for percentage rows — there is no stored value to drill into.
  category_id: z.string().nullable().optional().default(null),
  code: z.string(),
  name: z.string(),
  level: z.number(),
  category_type: z.string(),
  values: z.record(decimalSchema),
  total: decimalSchema,
  excel_code: z.string().nullable().optional().default(null),
  row_kind: z.enum(ROW_KINDS).optional().default('SOURCE'),
  parent_code: z.string().nullable().optional().default(null),
  ytd: decimalSchema.optional().default(null),
  is_calculated: z.boolean().optional().default(false),
});

export const pnlResponseSchema = z.object({
  business_unit: businessUnitRefSchema,
  year: z.number(),
  periods: z.array(periodRefSchema),
  rows: z.array(pnlRowSchema),
});

/** How the importer classified a row of the sheet. The report shows them all:
 * this is a mirror of the confirmed workbook, not a recomputation. */
export const SOURCE_ROW_KINDS = [
  'SOURCE_FINANCIAL',
  'AGGREGATE',
  'DERIVED_FORMULA',
  'PERCENTAGE',
  'HEADER',
  'INFORMATIONAL',
] as const;

export const pnlSourceRowSchema = z.object({
  row_index: z.number(),
  excel_code: z.string().nullable().optional().default(null),
  label: z.string(),
  level: z.number(),
  row_kind: z.string(),
  // Only rows that went through mapping can be drilled into.
  category_id: z.string().nullable().optional().default(null),
  values: z.record(decimalSchema),
  ytd: decimalSchema.optional().default(null),
});

/** «Отчёт ОПУ» — the confirmed workbook itself. Values here are the sheet's
 * own, including totals and formulas the canonical model never stores; they
 * must never be fed back into analytics. */
export const pnlSourceResponseSchema = z.object({
  business_unit: businessUnitRefSchema,
  import_id: z.string(),
  filename: z.string(),
  revision: z.number(),
  sheet_name: z.string(),
  periods: z.array(z.string()),
  rows: z.array(pnlSourceRowSchema),
});

export const expenseRowSchema = z.object({
  category_id: z.string(),
  code: z.string(),
  name: z.string(),
  amount: decimalSchema,
  share_of_expenses: decimalSchema,
  share_of_revenue: decimalSchema,
  mom: decimalSchema,
});

/** One canonical, non-overlapping spend group — cost of sales, payroll, the
 * operating-expense sections, turnover tax, extraordinary expenses. */
export const expenseGroupRowSchema = z.object({
  code: z.string(),
  name: z.string(),
  amount: decimalSchema,
  share_of_expenses: decimalSchema.optional().default(null),
  share_of_revenue: decimalSchema.optional().default(null),
  mom_abs: decimalSchema.optional().default(null),
  mom_pct: decimalSchema.optional().default(null),
});

export const expenseGroupMonthlyPointSchema = z.object({
  period_id: z.string(),
  year: z.number(),
  month: z.number(),
  label: z.string(),
  all_expenses: decimalSchema,
  groups: z.record(decimalSchema),
});

/** A manual line explaining what «Все расходы на др рестораны» is made of.
 * Never a second source of the total — see `extraordinaryExpenseReconciliationSchema`. */
export const extraordinaryExpenseDetailSchema = z.object({
  id: z.string(),
  business_unit_id: z.string(),
  financial_period_id: z.string(),
  expense_date: z.string(),
  amount: z.string(),
  expense_item: z.string(),
  target: z.string(),
  counterparty: z.string().nullable().optional().default(null),
  comment: z.string().nullable().optional().default(null),
  created_by: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
  author: z
    .object({ id: z.string(), full_name: z.string(), email: z.string() })
    .nullable()
    .optional()
    .default(null),
});

/** The Excel figure is the total; `details` only explain it. `remaining` is
 * null only when the canonical figure itself is unknown. */
export const extraordinaryExpenseReconciliationSchema = z.object({
  business_unit_id: z.string(),
  period_id: z.string(),
  canonical_total: decimalSchema,
  detailed_total: z.string(),
  remaining: decimalSchema,
  over_detailed: z.boolean(),
  details: z.array(extraordinaryExpenseDetailSchema).default([]),
});

export const expensesResponseSchema = z.object({
  business_unit: businessUnitRefSchema,
  period: periodRefSchema.nullable().optional().default(null),
  total: decimalSchema,
  rows: z.array(expenseRowSchema),

  // --- canonical redesign: restaurant units only ---
  all_expenses: expenseGroupRowSchema.nullable().optional().default(null),
  main_groups: z.array(expenseGroupRowSchema).default([]),
  monthly_series: z.array(expenseGroupMonthlyPointSchema).default([]),
  top_growth: z.array(expenseDriverRowSchema).default([]),
  top_decline: z.array(expenseDriverRowSchema).default([]),
  extraordinary: extraordinaryExpenseReconciliationSchema.nullable().optional().default(null),
});

/** Year-to-date, Jan → the selected month — never Aug–Dec, never the
 * workbook's own annual column. Margins are SUM(profit) / SUM(revenue). */
export const companyYtdSchema = z.object({
  year: z.number(),
  from_month: z.number(),
  to_month: z.number(),
  months: z.number(),
  restaurant_revenue: decimalSchema,
  restaurant_gross_profit: decimalSchema,
  restaurant_gross_margin: decimalSchema,
  restaurant_operating_profit: decimalSchema,
  restaurant_operating_margin: decimalSchema,
  restaurant_net_profit: decimalSchema,
  restaurant_net_margin: decimalSchema,
  restaurant_expenses: decimalSchema,
  group_payroll: decimalSchema,
  production_cost: decimalSchema,
  extraordinary_expenses: decimalSchema,
  residual: decimalSchema,
});

/** One confirmed month, summed across every active restaurant (and, for
 * `production_cost`, every production kitchen). */
export const companyMonthlyPointSchema = z.object({
  year: z.number(),
  month: z.number(),
  label: z.string(),
  restaurant_revenue: decimalSchema,
  restaurant_gross_profit: decimalSchema,
  restaurant_operating_profit: decimalSchema,
  restaurant_net_profit: decimalSchema,
  restaurant_expenses: decimalSchema,
  group_payroll: decimalSchema,
  production_cost: decimalSchema,
  extraordinary_expenses: decimalSchema,
  residual: decimalSchema,
});

export const companyOverviewSchema = z.object({
  period: periodRefSchema.nullable().optional().default(null),
  restaurant_revenue: decimalSchema,
  restaurant_gross_profit: decimalSchema,
  restaurant_operating_profit: decimalSchema,
  restaurant_net_profit: decimalSchema,
  restaurant_expenses: decimalSchema,
  production_cost: decimalSchema,
  // Actually posted allocations for the period — "0.00" when none exist.
  allocated_production_cost: decimalSchema,
  unallocated_production_cost: decimalSchema,
  // A configured rule allocates nothing by itself.
  allocation_configured: z.boolean(),
  allocation_complete: z.boolean(),
  allocation_warnings: z.array(z.record(z.unknown())).default([]),
  consolidated_expenses: decimalSchema,
  group_payroll: decimalSchema,
  active_business_units: z.number(),
  units: z.array(
    z.object({
      business_unit: businessUnitRefSchema,
      revenue: decimalSchema,
      net_profit: decimalSchema,
      total_expenses: decimalSchema,
      production_cost: decimalSchema,
    }),
  ),

  // --- year-to-date: the company's own annual management screen ---
  ytd: companyYtdSchema.nullable().optional().default(null),
  monthly_series: z.array(companyMonthlyPointSchema).default([]),
});

export const categorySchema = z.object({
  id: z.string(),
  code: z.string(),
  name_ru: z.string(),
  parent_id: z.string().nullable().optional().default(null),
  category_type: z.string(),
  business_unit_type: z.string().nullable().optional().default(null),
  level: z.number(),
  sort_order: z.number(),
});

export const unmappedRowSchema = z.object({
  id: z.string(),
  row_index: z.number(),
  raw_label: z.string(),
  normalized_label: z.string(),
  sheet_name: z.string(),
  business_unit_id: z.string().nullable(),
  mapping_status: z.string(),
  has_values: z.boolean(),
  suggested_category_id: z.string().nullable().optional().default(null),
});

export const importSheetSchema = z.object({
  id: z.string(),
  sheet_name: z.string(),
  sheet_index: z.number(),
  mapped_business_unit_id: z.string().nullable(),
  mapping_status: z.string(),
  detected_type: z.string().nullable(),
  detected_periods: z.array(z.string()).nullable().optional(),
});

/** Mirrors backend ImportStatus. NEEDS_MAPPING replaces the old
 *  "READY plus an error banner" pairing: one status, one next action. */
export const IMPORT_STATUS = [
  'UPLOADED',
  'PARSING',
  'VALIDATING',
  'NEEDS_MAPPING',
  'READY',
  'CONFIRMED',
  'FAILED',
  'ROLLED_BACK',
] as const;

export const importSchema = z.object({
  id: z.string(),
  filename: z.string(),
  file_hash: z.string(),
  sequence_number: z.number(),
  revision: z.number(),
  scope_key: z.string().nullable().optional(),
  scope_label: z.string().nullable().optional(),
  status: z.string(),
  quality_status: z.string(),
  uploaded_by: z.string(),
  uploaded_at: z.string(),
  confirmed_by: z.string().nullable().optional(),
  confirmed_at: z.string().nullable().optional(),
});

export const importDetailSchema = importSchema.extend({
  sheets: z.array(importSheetSchema).default([]),
  quality_report: z.record(z.unknown()).nullable().optional(),
});

export const qualityReportSchema = z.object({
  status: z.string().optional(),
  recognized_rows: z.number().optional(),
  total_rows: z.number().optional(),
  unknown_categories: z.array(z.string()).optional(),
  unknown_sheets: z.array(z.string()).optional(),
  detected_periods: z.array(z.string()).optional(),
  detected_units: z.array(z.string()).optional(),
  external_links: z.number().optional(),
  formula_errors: z.number().optional(),
  // Broken formulas in months the report does not cover — informational.
  future_formula_warnings: z.number().optional(),
  empty_values: z.number().optional(),
  unmapped_rows: z.number().optional(),
  warnings: z.array(z.record(z.unknown())).optional(),
  errors: z.array(z.record(z.unknown())).optional(),
});

export const importChangeSchema = z.object({
  business_unit_id: z.string(),
  financial_period_id: z.string(),
  // "2026-01", supplied by the API: a staged import has months to show before
  // any period is visible to the owner.
  period_key: z.string().nullable().optional().default(null),
  financial_category_id: z.string(),
  old_value: decimalSchema,
  new_value: decimalSchema,
  change_type: z.enum(['NEW', 'UPDATED', 'REMOVED', 'UNCHANGED']),
});

export type CurrentUser = z.infer<typeof currentUserSchema>;
export type BusinessUnit = z.infer<typeof businessUnitSchema>;
export type Period = z.infer<typeof periodSchema>;
export type KpiResponse = z.infer<typeof kpiResponseSchema>;
export type Money = z.infer<typeof moneySchema>;
export type MonthlySeriesPoint = z.infer<typeof monthlySeriesPointSchema>;
export type ExpenseDriverRow = z.infer<typeof expenseDriverRowSchema>;
export type CostCenterGroup = z.infer<typeof costCenterGroupSchema>;
export type CostCenterMonthlyPoint = z.infer<typeof costCenterMonthlyPointSchema>;
export type CostCenterYtd = z.infer<typeof costCenterYtdSchema>;
export type CostCenterAnalytics = z.infer<typeof costCenterAnalyticsSchema>;
export type HealthSummary = z.infer<typeof healthSummarySchema>;
export type PnlResponse = z.infer<typeof pnlResponseSchema>;
export type PnlRow = z.infer<typeof pnlRowSchema>;
export type PnlSourceResponse = z.infer<typeof pnlSourceResponseSchema>;
export type PnlSourceRow = z.infer<typeof pnlSourceRowSchema>;
export type ExpenseGroupRow = z.infer<typeof expenseGroupRowSchema>;
export type ExpenseGroupMonthlyPoint = z.infer<typeof expenseGroupMonthlyPointSchema>;
export type ExtraordinaryExpenseDetail = z.infer<typeof extraordinaryExpenseDetailSchema>;
export type ExtraordinaryExpenseReconciliation = z.infer<typeof extraordinaryExpenseReconciliationSchema>;
export type ExpensesResponse = z.infer<typeof expensesResponseSchema>;
export type CompanyYtd = z.infer<typeof companyYtdSchema>;
export type CompanyMonthlyPoint = z.infer<typeof companyMonthlyPointSchema>;
export type CompanyOverview = z.infer<typeof companyOverviewSchema>;
export type ImportJob = z.infer<typeof importSchema>;
export type ImportDetail = z.infer<typeof importDetailSchema>;
export type QualityReport = z.infer<typeof qualityReportSchema>;
export type ImportChange = z.infer<typeof importChangeSchema>;
export type FinancialCategoryItem = z.infer<typeof categorySchema>;
export type UnmappedRow = z.infer<typeof unmappedRowSchema>;
